import { ELEMENTS, type Element } from './constants'
import {
  BRANCHES,
  branchElement,
  branchesClash,
  branchesCombine,
  branchesTrine,
  relationOf,
  STEMS,
  stemElement,
  type Relation,
} from './ganzhi'

/**
 * 사주 글자에서 풀이 재료를 뽑는 순수 함수. 서버·클라이언트 공용.
 *
 * - 십성: 일간 기준으로 각 글자의 자리(비견·겁재·식신·상관·편재·정재·편관·정관·편인·정인).
 *   지지는 정기(본기) 천간으로 본다 — 子癸 丑己 寅甲 卯乙 辰戊 巳丙 午丁 未己 申庚 酉辛 戌戊 亥壬.
 * - 신살: 도화·역마·화개(연지·일지 삼합 기준), 천을귀인·문창귀인(일간 기준), 백호·괴강(기둥).
 *   널리 쓰이는 기본 판정만 쓴다. 화면·AI 에는 이름 대신 쉬운 설명과 긍정적인 말로 내보낸다.
 */

export type PillarKey = 'year' | 'month' | 'day' | 'hour'
export const PILLAR_KEYS: readonly PillarKey[] = ['year', 'month', 'day', 'hour']

export type TenGod =
  | 'bijeon' // 비견
  | 'geopjae' // 겁재
  | 'siksin' // 식신
  | 'sanggwan' // 상관
  | 'pyeonjae' // 편재
  | 'jeongjae' // 정재
  | 'pyeongwan' // 편관
  | 'jeonggwan' // 정관
  | 'pyeonin' // 편인
  | 'jeongin' // 정인

const GROUP: Record<TenGod, Relation> = {
  bijeon: 'same',
  geopjae: 'same',
  siksin: 'output',
  sanggwan: 'output',
  pyeonjae: 'wealth',
  jeongjae: 'wealth',
  pyeongwan: 'officer',
  jeonggwan: 'officer',
  pyeonin: 'resource',
  jeongin: 'resource',
}

export function groupOf(god: TenGod): Relation {
  return GROUP[god]
}

const BRANCH_MAIN_STEM = '癸己甲乙戊丙丁己庚辛戊壬'

function stemPolarityYang(stem: string): boolean {
  return STEMS.indexOf(stem) % 2 === 0
}

/** 일간 기준 한 글자(천간 또는 지지)의 십성 */
export function tenGodOf(dayStem: string, char: string): TenGod {
  const stem = STEMS.includes(char) ? char : BRANCH_MAIN_STEM[BRANCHES.indexOf(char)]
  if (!stem || !STEMS.includes(dayStem)) throw new Error(`글자가 아니다: ${char}`)
  const rel = relationOf(stemElement(dayStem), stemElement(stem))
  const samePolarity = stemPolarityYang(dayStem) === stemPolarityYang(stem)
  switch (rel) {
    case 'same':
      return samePolarity ? 'bijeon' : 'geopjae'
    case 'output':
      return samePolarity ? 'siksin' : 'sanggwan'
    case 'wealth':
      return samePolarity ? 'pyeonjae' : 'jeongjae'
    case 'officer':
      return samePolarity ? 'pyeongwan' : 'jeonggwan'
    case 'resource':
      return samePolarity ? 'pyeonin' : 'jeongin'
  }
}

export interface ChartPillars {
  year: string
  month: string
  day: string
  /** 시간 모름이면 null */
  hour: string | null
}

export interface Placement {
  pillar: PillarKey
  part: 'stem' | 'branch'
  char: string
  god: TenGod
}

/** 일간을 뺀 모든 글자의 십성 자리 */
export function placements(p: ChartPillars): Placement[] {
  const dayStem = p.day[0]!
  const out: Placement[] = []
  for (const key of PILLAR_KEYS) {
    const gz = p[key]
    if (!gz) continue
    const [stem, branch] = [...gz] as [string, string]
    if (key !== 'day') out.push({ pillar: key, part: 'stem', char: stem, god: tenGodOf(dayStem, stem) })
    out.push({ pillar: key, part: 'branch', char: branch, god: tenGodOf(dayStem, branch) })
  }
  return out
}

export function groupCounts(list: Placement[]): Record<Relation, number> {
  const counts: Record<Relation, number> = { same: 0, output: 0, wealth: 0, officer: 0, resource: 0 }
  for (const p of list) counts[GROUP[p.god]] += 1
  return counts
}

/** 일간의 힘: 나를 돕는 글자(비겁·인성)가 절반 이상이면 strong. 월지(계절)는 두 배로 센다. */
export function dayMasterStrength(p: ChartPillars): 'strong' | 'balanced' | 'light' {
  const list = placements(p)
  let help = 0
  let total = 0
  for (const x of list) {
    const w = x.pillar === 'month' && x.part === 'branch' ? 2 : 1
    total += w
    if (GROUP[x.god] === 'same' || GROUP[x.god] === 'resource') help += w
  }
  const ratio = help / total
  return ratio >= 0.5 ? 'strong' : ratio >= 0.33 ? 'balanced' : 'light'
}

// 신살

export type Shinsal = 'dohwa' | 'yeokma' | 'hwagae' | 'cheoneul' | 'munchang' | 'baekho' | 'goegang'
export const SHINSALS: readonly Shinsal[] = ['dohwa', 'yeokma', 'hwagae', 'cheoneul', 'munchang', 'baekho', 'goegang']

const TRINE_GROUPS = ['申子辰', '寅午戌', '巳酉丑', '亥卯未'] as const
const DOHWA: Record<(typeof TRINE_GROUPS)[number], string> = { 申子辰: '酉', 寅午戌: '卯', 巳酉丑: '午', 亥卯未: '子' }
const YEOKMA: Record<(typeof TRINE_GROUPS)[number], string> = { 申子辰: '寅', 寅午戌: '申', 巳酉丑: '亥', 亥卯未: '巳' }
const HWAGAE: Record<(typeof TRINE_GROUPS)[number], string> = { 申子辰: '辰', 寅午戌: '戌', 巳酉丑: '丑', 亥卯未: '未' }

/** 천을귀인: 甲戊庚→丑未, 乙己→子申, 丙丁→亥酉, 辛→寅午, 壬癸→巳卯 */
const CHEONEUL: Record<string, string> = { 甲: '丑未', 戊: '丑未', 庚: '丑未', 乙: '子申', 己: '子申', 丙: '亥酉', 丁: '亥酉', 辛: '寅午', 壬: '巳卯', 癸: '巳卯' }
/** 문창귀인: 甲巳 乙午 丙申 丁酉 戊申 己酉 庚亥 辛子 壬寅 癸卯 */
const MUNCHANG: Record<string, string> = { 甲: '巳', 乙: '午', 丙: '申', 丁: '酉', 戊: '申', 己: '酉', 庚: '亥', 辛: '子', 壬: '寅', 癸: '卯' }
const BAEKHO = ['甲辰', '乙未', '丙戌', '丁丑', '戊辰', '壬戌', '癸丑']
const GOEGANG = ['庚辰', '庚戌', '壬辰', '壬戌', '戊戌']

export interface ShinsalHit {
  name: Shinsal
  pillar: PillarKey
}

function groupOfBranch(branch: string): (typeof TRINE_GROUPS)[number] {
  return TRINE_GROUPS.find((g) => g.includes(branch))!
}

export function shinsals(p: ChartPillars): ShinsalHit[] {
  const hits: ShinsalHit[] = []
  const add = (name: Shinsal, pillar: PillarKey) => {
    if (!hits.some((h) => h.name === name && h.pillar === pillar)) hits.push({ name, pillar })
  }
  const dayStem = p.day[0]!
  // 도화·역마·화개는 연지와 일지를 기준으로, 기준 기둥이 아닌 다른 기둥의 지지에서 찾는다.
  const bases: [PillarKey, string][] = [['year', p.year[1]!], ['day', p.day[1]!]]
  for (const key of PILLAR_KEYS) {
    const gz = p[key]
    if (!gz) continue
    const branch = gz[1]!
    for (const [baseKey, base] of bases) {
      if (baseKey === key) continue
      const g = groupOfBranch(base)
      if (branch === DOHWA[g]) add('dohwa', key)
      if (branch === YEOKMA[g]) add('yeokma', key)
      if (branch === HWAGAE[g]) add('hwagae', key)
    }
    if (CHEONEUL[dayStem]!.includes(branch)) add('cheoneul', key)
    if (MUNCHANG[dayStem] === branch) add('munchang', key)
    if (BAEKHO.includes(gz)) add('baekho', key)
    if (GOEGANG.includes(gz) && key !== 'year') add('goegang', key)
  }
  return hits
}

export function shinsalNames(p: ChartPillars): Shinsal[] {
  return SHINSALS.filter((s) => shinsals(p).some((h) => h.name === s))
}

// 기둥 사이 관계, 오행 균형

export interface BranchLink {
  with: PillarKey
  kind: 'combine' | 'clash' | 'trine'
}

/** 일지(배우자 자리)와 다른 지지의 합·충·삼합 */
export function dayBranchLinks(p: ChartPillars): BranchLink[] {
  const dayBranch = p.day[1]!
  const out: BranchLink[] = []
  for (const key of ['year', 'month', 'hour'] as const) {
    const b = p[key]?.[1]
    if (!b) continue
    if (branchesCombine(dayBranch, b)) out.push({ with: key, kind: 'combine' })
    else if (branchesClash(dayBranch, b)) out.push({ with: key, kind: 'clash' })
    else if (branchesTrine(dayBranch, b)) out.push({ with: key, kind: 'trine' })
  }
  return out
}

export function elementCounts(p: ChartPillars): Record<Element, number> {
  const counts = Object.fromEntries(ELEMENTS.map((e) => [e, 0])) as Record<Element, number>
  for (const key of PILLAR_KEYS) {
    const gz = p[key]
    if (!gz) continue
    counts[stemElement(gz[0]!)] += 1
    counts[branchElement(gz[1]!)] += 1
  }
  return counts
}

/** 한 간지(대운·세운·월운)가 원국과 맺는 관계 요약 */
export function periodLinks(p: ChartPillars, ganZhi: string): { stemGod: TenGod; branchGod: TenGod; clashes: PillarKey[]; combines: PillarKey[] } {
  const dayStem = p.day[0]!
  const [stem, branch] = [...ganZhi] as [string, string]
  const clashes: PillarKey[] = []
  const combines: PillarKey[] = []
  for (const key of PILLAR_KEYS) {
    const b = p[key]?.[1]
    if (!b) continue
    if (branchesClash(b, branch)) clashes.push(key)
    if (branchesCombine(b, branch)) combines.push(key)
  }
  return { stemGod: tenGodOf(dayStem, stem), branchGod: tenGodOf(dayStem, branch), clashes, combines }
}

/** 직장(조직 안에서 일하기)과 창업(내 일을 꾸리기) 중 어느 쪽이 더 맞나 */
export type WorkPath = 'job' | 'own' | 'both'

/**
 * **직장 vs 창업 기울기**. 재물 장의 '직장이 맞을까, 창업이 맞을까' 칸과 사업 장이 같은 판정을 받는다 —
 * 따로 정하면 한 리포트 안에서 "창업이 맞다" 와 "사업은 작게만" 이 엇갈린다.
 * 만드는 재주(식상)와 돈 버는 감각(재성)이 함께 있고 틀·책임(관성)·뒷받침(인성)보다 둘 이상 많으면 창업 쪽,
 * 그 반대로 둘 이상 많으면 직장 쪽, 나머지는 직장에 다니며 작게 곁들이는 쪽.
 */
export function workPathLean(p: ChartPillars): WorkPath {
  const n = groupCounts(placements(p))
  const own = n.output + n.wealth
  const org = n.officer + n.resource
  if (n.output > 0 && n.wealth > 0 && own >= org + 2) return 'own'
  if (org >= own + 2) return 'job'
  return 'both'
}
